<template>
	<div class="bg-accented py-12 sm:py-16 mt-4 w-full min-w-screen max-w-screen relative isolate overflow-hidden">
		<div>
			<h2 class="pb-12 sm:pb-16 text-5xl flex flex-col tracking-tight text-center">
				<span class="text-primary  font-semibold">
					MAVERIX’s
				</span>
				<span class="text-highlighted">
					{{ $t('service-area.carousel.title') }}
				</span>
			</h2>
			<div class="max-w-[1600px] mx-auto">
				<UCarousel
					v-if="hasAreas"
					ref="service-areas-carousel"
					v-slot="{ item: area, index }"
					:autoplay="{ delay: 4000 }"
					:items="areas"
					loop
					:ui="{
						item: 'basis-1/3 md:basis-1/4 group min-w-[75%] w-full sm:w-auto sm:min-w-[350px] md:min-w-[350px] lg:min-w-[400px]',
						container: 'py-6'
					}"
					arrows
					:prev="{ color: 'primary', variant: 'soft' }"
					:next="{ color: 'primary', variant: 'soft' }"
					:in-view-threshold="0.5"
					class-names
					wheel-gestures
					class="h-[340px] md:h-[380px] w-full sm:mask-linear-[90deg,transparent_5%,black,transparent_95%]"
				>
					<ServiceAreasCityCard
						:key="index"
						:service-area="area"
						:class="[
							'flex flex-col items-center -ml-3 sm:ml-0 justify-center cursor-pointer transform transition-all duration-300 delay-150 ease-in-out',

							// Default for .is-in-view (items further away)
							'group:opacity-8 group:mb-12',

							// Styles for the item BEFORE the .is-snapped item
							'group-[&:has(+div.is-snapped)]:opacity-45 group-[&:has(+div.is-snapped)]:mb-10 select-none',

							// Styles for the .is-snapped item itself
							'group-[.is-snapped]:bg-white group-[.is-snapped]:pb-7 group-[.is-snapped]:-mt-3 group-[.is-snapped]:transform group-[.is-snapped]:scale-[105%] group-[.is-snapped]:border-b-8 group-[.is-snapped]:shadow-md group-[.is-snapped]:border-b-(--ui-primary) group-[.is-snapped]:opacity-100 group-[.is-snapped]:mb-0',

							// Styles for the item AFTER the .is-snapped item
							// (using the '+&' selector since there is no CSS selector for 'the element after me')
							'group-[.is-snapped+&]:opacity-45 group-[.is-snapped+&]:mb-10 select-none'
						]"
						image-classes="m-2 w-full px-2"
						:card-active="selectedSlideIndex === index"
						@click.prevent.stop="cardClicked(index)"
					/>
				</UCarousel>
			</div>
			<div class="flex justify-center mt-6 mx-4">
				<UButton
					:label="$t('service-area.carousel.link')"
					:to="localePath('/service-areas')"
					size="xl"
					:ui="{
						base: 'block sm:inline-flex text-center justify-center'
					}"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useI18n } from 'vue-i18n'

const { bugsnagReport } = useErrorReporter()

const { locale } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

const stories = useState<any[]>(`service-areas-carousel-${locale.value}`, () => [])

// If no serviceAreasStories found in SSG payload,
// then fetch via server API during SSR to avoid client-side Storyblok calls
if (!stories.value?.length) {
	const {
		data, error
	} = await useFetch('/api/service-areas', {
		params: { language: locale.value }
	})
	if (error.value) {
		bugsnagReport({
			error: new Error('API Error - No service areas found'),
			showToast: true
		})
	}
	stories.value = data.value || []
	if (!stories.value?.length) {
		bugsnagReport({
			error: new Error('No service areas found in Storyblok'),
			showToast: true
		})
	}
}

const areas = computed(() => {
	return stories?.value?.map((obj: any) => {
		const {
			title, subtitle, comingSoon, comingSoonText, image
		} = obj.content?.cityCardContent?.[0] || {
			title: '',
			subtitle: '',
			comingSoon: false,
			comingSoonText: '',
			image: ''
		}
		return {
			id: obj.uuid,
			link: `/${obj.full_slug}`,
			title,
			subtitle,
			comingSoon,
			comingSoonText,
			image
		}
	})
})

const hasAreas = computed(() => {
	return !!areas.value?.length
})

const selectedSlideIndex = ref(0)

const handleScroll = () => {
	selectedSlideIndex.value = (carousel.value?.emblaApi?.slidesInView()[0] ?? 0)
}

const carousel = useTemplateRef('service-areas-carousel')

const cardClicked = (index: number) => {
	carousel.value?.emblaApi?.scrollTo(index)
	setTimeout(() => {
		selectedSlideIndex.value = index
	}, 200)
}

onMounted(() => {
	if (carousel.value?.emblaApi) {
		carousel.value.emblaApi.on('slidesInView', handleScroll)
	}
})
</script>
